'use client';

import { Fragment } from 'react';
import styles from './BusinessValues.module.scss';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';

interface Value {
  icon: 'finish' | 'transparency' | 'accountability' | 'agility';
  title: string;
  description: string;
}

interface BusinessValuesProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  values?: Value[];
  backgroundImage?: string;
  tilt?: 'left' | 'right';
  id?: string;
}

const ICONS: Record<Value['icon'], () => JSX.Element> = {
  finish: () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  transparency: () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  accountability: () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 12 15 16 10" />
    </svg>
  ),
  agility: () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2z" />
    </svg>
  ),
};

const DEFAULT_VALUES: Value[] = [
  {
    icon: 'finish',
    title: 'Finir le travail',
    description:
      'On ne livre pas un chantier à moitié. Chaque livrable est terminé, testé, documenté et prêt pour la production.',
  },
  {
    icon: 'transparency',
    title: 'Transparence',
    description:
      'Tableaux de bord, métriques objectives et accès direct aux outils. Vous voyez l’avancée en temps réel, sans filtre.',
  },
  {
    icon: 'accountability',
    title: 'Accountability',
    description:
      'Un interlocuteur unique porte la responsabilité du résultat final. Pas de renvois d’appels, pas de perte d’information.',
  },
  {
    icon: 'agility',
    title: 'Agility',
    description:
      'Les bonnes compétences sont mobilisées quand vous en avez besoin. Le pilotage s’adapte à vos priorités, pas l’inverse.',
  },
];

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function BusinessValues({
  eyebrow = 'NOS VALEURS',
  title = 'Ce qui guide chaque livraison :',
  highlightedTitle = '*exigence, clarté, responsabilité*',
  description = 'Quatre principes simples qui transforment une prestation en partenariat.',
  values = DEFAULT_VALUES,
  backgroundImage,
  tilt = 'right',
  id,
}: BusinessValuesProps) {
  useAOS();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;
  const hasBgClass = backgroundImage ? styles.hasBg : '';

  return (
    <div
      id={id}
      className={`${styles.root} ${tiltClass} ${hasBgClass}`}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          <h2 className={styles.title}>
            {renderTitle(title)} {renderTitle(highlightedTitle)}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>

        <div
          className={styles.grid}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
        >
          {values.map((value) => {
            const Icon = ICONS[value.icon];
            return (
              <div
                key={value.title}
                className={styles.card}
                onClick={() => gaClick('business_values_card', { value_title: value.title })}
              >
                <div className={styles.icon}>
                  <Icon />
                </div>
                <h3 className={styles.cardTitle}>{value.title}</h3>
                <p className={styles.cardDesc}>{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
